/* RESPONSIBLE TEAM: team-data-foundations */
import { attr, belongsTo, hasMany } from '@ember-data/model';
import Admin from 'embercom/models/admin';
//@ts-ignore no type declaration available for array
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import { isPresent, isEmpty } from '@ember/utils';
import { validator, buildValidations } from 'ember-cp-validations';
import { humanize } from 'ember-cli-string-helpers/helpers/humanize';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';

//@ts-ignore no type declaration available for ember-copy
import { copy } from 'ember-copy';
import type ActionObjectMapping from './action-object-mapping';
import type ActionHeader from './action-header';
import type ParamDescriptor from './param-descriptor';
import type TestResult from './test-result';
import type ResponseField from './response-field';
import type MockResponse from './mock-response';
import type SuggestedQuestion from './suggested-question';
import { BaseAction } from 'embercom/models/workflow-connector/base-action';
import _ from 'underscore';
import type ApiMappableObject from 'embercom/models/workflow-connector/api-mappable-object';
import type ContextualIdentifierConfig from './customer-data-verification/contextual-identifier-config';
import type CodeBlockExecutionTestResult from 'embercom/models/workflow-connector/code-block-execution-test-result';
import type AppService from 'embercom/services/app-service';

const Validations = buildValidations({
  name: [
    validator('presence', {
      presence: true,
    }),
  ],
  url: [
    validator('presence', {
      presence: true,
      messageKey: 'workflow-connector.builder.body.request.url.https',
    }),
    validator('format', { type: 'url' }),
  ],
  description: [
    validator('presence', {
      presence: true,
      disabled: _.isEqual('model.usage', 'workflow_and_inbox'),
    }),
  ],
});

export enum ContentType {
  JSON = 'application/json',
  TEXT = 'text/plain',
  FORM_URLENCODED = 'application/x-www-form-urlencoded',
  CUSTOM = 'custom',
}

export default class Action extends BaseAction.extend(Validations) {
  static readonly DATA_TRANSFORMATION_TYPES = {
    FULL_ACCESS: 'full_access',
    REDACTED_ACCESS: 'redacted_access',
    CODE_BLOCK_TRANSFORMATION: 'code_block_transformation',
  } as const;

  @attr('string') declare name: string;
  @attr('string') declare description: string;
  @attr('string') declare state: string;
  @attr('string') declare url: string;
  @attr('string') declare httpMethod: string;
  @attr('string') declare body: string | null;
  @attr('string') declare codeExecutionBlock: string | null;
  @attr('boolean') declare isTemplate: boolean;
  @attr('boolean', { defaultValue: false }) declare isRecommendedAction: boolean;
  @attr('string') declare tokenId: string | null;
  @attr('array', { defaultValue: () => [] }) declare tokenIds: (string | null)[];
  @attr('string') declare mcpServerId: string | null;
  @attr('date') declare createdAt: Date;
  @attr('date') declare updatedAt: Date;
  @attr('number') declare createdByAdminId: number;
  @attr('number') declare updatedByAdminId: number;
  @attr('string') declare appPackageCode: string;
  @attr('boolean') declare finEnabled: boolean;
  @attr('string') declare usage: string;
  @attr('string') declare iconUrl: string;
  @attr('number') declare sentCount: number;
  @attr('number') declare resolvedCount: number;
  @attr('boolean') declare includeFullResponse: boolean;
  @attr('string', { defaultValue: 'full_access' })
  declare dataTransformationType: string;
  @attr('number') declare conversationUsage: number;
  @attr('string') declare configurationResponseType: string;
  @attr('boolean') declare finWriteAction: boolean;
  @attr('string') declare placeholderUrl: string;
  @attr('boolean') declare otpAuthEnabled: boolean;
  @attr('boolean', { allowNull: true }) declare noAuthenticationChosen: boolean | null;
  @attr('boolean', { defaultValue: true }) declare validateMissingAttributes: boolean;
  @fragmentArray('workflow-connector/action-header') declare headers: ActionHeader[];
  @fragmentArray('workflow-connector/action-object-mapping')
  declare objectMappings: ActionObjectMapping[];
  @fragmentArray('workflow-connector/param-descriptor') declare paramDescriptors: ParamDescriptor[];
  @fragment('workflow-connector/test-result') declare testResult: TestResult;
  @fragment('workflow-connector/mock-response') declare mockResponse: MockResponse;
  @fragmentArray('workflow-connector/response-field') declare responseFields: ResponseField[];
  @fragmentArray('workflow-connector/suggested-question')
  declare suggestedQuestions: SuggestedQuestion[];
  @fragmentArray('workflow-connector/api-mappable-object')
  declare apiMappableObjects: ApiMappableObject[];
  @fragment('workflow-connector/customer-data-verification/contextual-identifier-config')
  declare contextualIdentifierConfig: ContextualIdentifierConfig;
  @fragment('workflow-connector/code-block-execution-test-result')
  declare codeBlockExecutionTestResult: CodeBlockExecutionTestResult | null;

  @hasMany('workflow-connector/action', { async: false }) declare children: Action[];
  @belongsTo('workflow-connector/action', { async: false, inverse: 'children' })
  declare parent: Action;

  // Fin Audience (ContentSegment) support
  @attr('array', { defaultValue: () => [] }) declare aiContentSegmentIds: number[];

  @service declare attributeService: any;
  @service declare appService: AppService;
  @tracked attributeData: Record<string, { name: string; type: string }> = {};

  @tracked audienceMode: 'everyone' | 'custom' | 'fin_audience' = 'everyone';
  @tracked selectedSegmentIds: number[] = [];

  // Original baselines for change detection
  private _originalAudienceMode: 'everyone' | 'custom' | 'fin_audience' = 'everyone';
  private _originalSelectedSegmentIds: number[] = [];

  didLoad() {
    this.hydrateMappingObjects();
    this.setTestedRequest();
    this.setDefaultRuleset();
    this.attributeData = this.getAttributeNamesAndTypes();
    this._initializeAudienceStateFromServerData();
  }

  _initializeAudienceStateFromServerData() {
    if (this.aiContentSegmentIds && this.aiContentSegmentIds.length > 0) {
      this.audienceMode = 'fin_audience';
      this.selectedSegmentIds = [...this.aiContentSegmentIds];
    } else if (this._hasActiveLiveRuleset()) {
      this.audienceMode = 'custom';
      this.selectedSegmentIds = [];
    } else {
      this.audienceMode = 'everyone';
      this.selectedSegmentIds = [];
    }

    // Set baselines for change detection
    this._originalAudienceMode = this.audienceMode;
    this._originalSelectedSegmentIds = [...this.selectedSegmentIds];
  }

  private _hasActiveLiveRuleset(): boolean {
    if (!this.ruleset) {
      return false;
    }
    let isLive = this.ruleset.isLive;
    let hasPredicates = this.ruleset.predicateGroup?.predicates?.length > 0;
    let hasRolePredicates = this.ruleset.rolePredicateGroup?.predicates?.length > 0;
    return isLive && (hasPredicates || hasRolePredicates);
  }

  didUpdate() {
    super.didUpdate?.();
    // Reset baselines after successful save
    this._originalAudienceMode = this.audienceMode;
    this._originalSelectedSegmentIds = [...this.selectedSegmentIds];
  }

  setNoAuthenticationChosen(value: boolean | null) {
    this.set('noAuthenticationChosen', value);
  }

  get createdByAdmin() {
    return Admin.peekAndMaybeLoad(this.store, this.createdByAdminId?.toString());
  }

  get updatedByAdmin() {
    return Admin.peekAndMaybeLoad(this.store, this.updatedByAdminId?.toString());
  }

  get canBeSetLive() {
    // TODO connect to validations
    // add canBeSaved()
    return this.state === 'draft' && this.isValid;
  }

  // TODO rename
  get attributes() {
    let parameterRegex = new RegExp(/{{(.*?)}}/g);
    let text = this.url || '';
    if (['post', 'put', 'patch', 'delete'].includes(this.httpMethod)) {
      text += this.body;
    }

    if (this.appService.app.canUseFeature('data-connector-header-templating')) {
      text += (this.headers || []).map((h) => h.value || '').join(' ');
    }

    if (this.httpMethod === 'get' && this.mcpServerId) {
      text += Object.keys(JSON.parse(this.body || '{}')?.arguments || {})
        .map((key) => `{{${key}}}`)
        .join('');
    }

    return text.matchAll(parameterRegex);
  }

  get usedAttributeArray(): string[] {
    return [...this.attributes].map((match) => match[1]);
  }

  get unUsedParamDescriptors(): ParamDescriptor[] {
    return this.paramDescriptors.filter(
      (descriptor) => !this.usedAttributeArray.includes(descriptor.name) && !this.mcpServerId,
    );
  }

  get actionTested() {
    // TODO check if test result is successful
    return isPresent(this.testResult?.actionResponse);
  }

  get actionNotTested() {
    return !this.actionTested;
  }

  get apiMappableObjectsPresent() {
    return isPresent(this.apiMappableObjects);
  }

  // @ts-ignore this should be renamed to no longer override the built-in isValid property
  get isValid() {
    return (
      this.isGeneralConfigurationValid &&
      this.isRequestConfigurationValid &&
      this.hasValidResponse &&
      this.isResponseConfigurationValid
    );
  }

  get isGeneralConfigurationValid() {
    if (!this.isFinStandaloneAction) {
      return this.validations.attrs.name.isValid;
    }

    return (
      this.validations.attrs.name.isValid &&
      this.validations.attrs.description.isValid &&
      this.isAudienceValid
    );
  }

  get isRequestConfigurationValid() {
    return (
      !this.installUrlErrors && this.isRequestBodyValid && this.unUsedParamDescriptors.length === 0
    );
  }

  get isRequestBodyValid() {
    let contentTypeHeader = this.headers?.find((h) => h.name === 'Content-Type');

    if (contentTypeHeader?.value !== ContentType.JSON) {
      return true;
    }

    if (!this.body) {
      return true;
    }

    return this.isValidJsonBody(this.body);
  }

  private isValidJsonBody(requestBodyString: string): boolean {
    try {
      let validateStr = requestBodyString.replaceAll('<br>', '');
      let parameterRegex = new RegExp(/{{(.*?)}}/g);
      let matches = [...validateStr.matchAll(parameterRegex)];

      matches.forEach((match) => {
        let attributeName = match[1].trim();
        let templateVar = match[0];
        let attributeType = this.getAttributeType(attributeName);
        let replacement = this.getReplacementValueForType(attributeType, templateVar);

        validateStr = validateStr.replaceAll(templateVar, replacement);
      });

      JSON.parse(validateStr);
      return true;
    } catch (e) {
      return false;
    }
  }

  private getAttributeType(attributeName: string): string {
    let paramDescriptor = this.paramDescriptors.find((param) => param.name === attributeName);
    if (paramDescriptor) {
      return (paramDescriptor.type || '').toLowerCase();
    }
    return (this.attributeData[attributeName]?.type || '').toLowerCase();
  }

  private getReplacementValueForType(attributeType: string, templateVariable: string): string {
    switch (true) {
      case ['integer', 'decimal', 'number', 'float', 'last_conversation_rating_given'].includes(
        attributeType,
      ):
        return '0';

      case attributeType === 'boolean':
        return 'true';

      case attributeType === 'conversation_attribute_list':
        return '[]';

      case attributeType === 'user_event':
        return '{}';

      case attributeType.endsWith('_id'):
        return '1000';

      default:
        return templateVariable;
    }
  }

  get hasValidResponse() {
    return (this.isTestResponse && this.testIsValid) || (this.isMockResponse && this.mockIsValid);
  }

  get isResponseMappingValid() {
    return isEmpty(this.responseMappingValidationErrors);
  }

  get responseAttributesSelectionValid(): boolean {
    if (!this.isFinStandaloneAction) {
      return true;
    }

    if (this.includeFullResponse) {
      return true;
    }

    return this.responseFields.any((field) => !field.redacted);
  }

  @dependentKeyCompat
  get isCodeBlockValid(): boolean {
    // If data transformation type is code block but no code exists, it's invalid
    if (
      this.dataTransformationType === Action.DATA_TRANSFORMATION_TYPES.CODE_BLOCK_TRANSFORMATION
    ) {
      if (!this.codeExecutionBlock || this.codeExecutionBlock.trim().length === 0) {
        return false;
      }
    }

    // If not using code block transformation, code block validation passes
    if (!this.codeExecutionBlock || this.codeExecutionBlock.trim().length === 0) {
      return true;
    }

    // If code block exists but hasn't been tested, it's invalid
    if (!this.codeBlockExecutionTestResult) {
      return false;
    }

    // If test failed (status is not 200), it's invalid
    if (this.codeBlockExecutionTestResult.response?.status !== 200) {
      return false;
    }

    // Code block must return a response body
    if (!this.codeBlockExecutionTestResult.response?.body) {
      return false;
    }

    // Validate the response body is proper JSON
    try {
      JSON.stringify(this.codeBlockExecutionTestResult.response.body);
      return true;
    } catch (e) {
      return false;
    }
  }

  @dependentKeyCompat
  get isObjectMappingSectionEnabled(): boolean {
    // Must have valid response
    if (!this.hasValidResponse) {
      return false;
    }

    // If not using code block transformation, allow navigation
    if (
      this.dataTransformationType !== Action.DATA_TRANSFORMATION_TYPES.CODE_BLOCK_TRANSFORMATION
    ) {
      return true;
    }

    // If using code block transformation, check if execution was successful
    return this.codeBlockExecutionTestResult?.response?.status === 200;
  }

  @dependentKeyCompat
  get isResponseConfigurationValid(): boolean {
    if (
      this.dataTransformationType === Action.DATA_TRANSFORMATION_TYPES.CODE_BLOCK_TRANSFORMATION
    ) {
      return this.isCodeBlockValid;
    }
    return this.isResponseMappingValid && this.responseAttributesSelectionValid;
  }

  get hasUnsavedChanges() {
    let hasAudienceChanges = this.appService?.app?.canUseFeature?.(
      'tdf-migrate-actions-to-fin-audience',
    )
      ? this.hasAudienceChanges
      : false;

    return (
      this.hasDirtyAttributes ||
      (this.isFinStandaloneAction && this.ruleset?.hasDirtyAttributes) ||
      hasAudienceChanges
    );
  }

  get hasAttributeChangesThatAffectPreview() {
    let changedAttributes = this.changedAttributes();
    let isResponseFieldsChanged =
      changedAttributes.responseFields &&
      !_.isEqual(changedAttributes.responseFields[1], this.responseFields);

    return (
      isResponseFieldsChanged ||
      this.mockResponse?.hasDirtyAttributes ||
      this.testResult?.hasDirtyAttributes
    );
  }

  get isAudienceValid() {
    if (!this.isFinStandaloneAction) {
      return true;
    }

    if (this.appService?.app?.canUseFeature?.('tdf-migrate-actions-to-fin-audience')) {
      switch (this.audienceMode) {
        case 'everyone':
          return true;
        case 'fin_audience':
          return this.selectedSegmentCount > 0;
        case 'custom':
          return this.ruleset?.audienceValid;
        default:
          return false;
      }
    }

    // Original validation logic when FF is off
    return this.ruleset?.audienceValid || false;
  }

  get isSecuritySettingsValid() {
    // action needs to have at least one authentication method enabled,
    // or noAuthenticationChosen needs to be set to true (meaning admin chose to not use authentication, accepted the risks)
    return (
      this.otpAuthEnabled || this.isMessengerInstalledAndProtected || this.noAuthenticationChosen
    );
  }

  get installUrlErrors() {
    return this.validations.errors?.findBy('attribute', 'url');
  }

  get responseMappingValidationErrors() {
    let objectMappingsValidationResult = this.objectMappings
      .map((objectMapping) => objectMapping.validationErrors)
      .flat();

    if (isPresent(objectMappingsValidationResult)) {
      return ['invalid_object_mappings'];
    }

    return [];
  }

  get requiredAttributesWarningPresent() {
    let objectMappingWarnings = this.objectMappings
      .map((objectMapping) => objectMapping.missingRequiredAttributeMappingWarnings)
      .flat();

    if (isPresent(objectMappingWarnings)) {
      return true;
    }
    return false;
  }

  get isTestResponse() {
    return this.configurationResponseType === 'test_response_type';
  }

  get isMockResponse() {
    return this.configurationResponseType === 'mock_response_type';
  }

  get testIsValid() {
    if (isPresent(this.testResult?.actionResponse)) {
      if (this.isJsonInvalid) {
        return false;
      } else if (!this.hasDirtyAttributes) {
        return true;
      } else if (isPresent(this.testResult.testedAction)) {
        return this.isTestActionValid;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  get isJsonInvalid() {
    if (!this.isFinStandaloneAction) {
      return false;
    }

    // If we have a valid parsed body object, we already know the JSON is valid.
    if (
      this.testResult?.actionResponse?.body !== null &&
      this.testResult?.actionResponse?.body !== undefined
    ) {
      return false;
    }

    if (!this.testResult?.actionResponse?.rawBody) {
      return false;
    }

    try {
      JSON.parse(this.testResult?.actionResponse?.rawBody);
    } catch (e) {
      return true;
    }
    return false;
  }

  get mockIsValid() {
    try {
      JSON.parse(this.mockResponse?.body);
    } catch (e) {
      return false;
    }
    return true;
  }

  get isTestActionValid() {
    let tokenIdsMatch = _.isEqual(this.tokenIds, this.testResult.testedAction.tokenIds || []);

    return (
      this.httpMethod === this.testResult.testedAction.httpMethod &&
      this.url === this.testResult.testedAction.url &&
      this.headers === this.testResult.testedAction.headers &&
      this.body === this.testResult.testedAction.body &&
      this.tokenId === this.testResult.testedAction.tokenId &&
      tokenIdsMatch
    );
  }

  get shouldRerunTest() {
    return !(this.testIsValid || !isPresent(this.testResult?.actionResponse));
  }

  hydrateMappingObjects() {
    // this wires up objectMappings and attributeMappings with respective apiMappableObjects and apiMappableAttributes
    this.objectMappings.forEach((objectMapping) => {
      objectMapping.hydrateParent(this);
    });
  }

  setTestedRequest() {
    if (!this.testResult) {
      // @ts-ignore we should initialize this instead of setting it to an empty object
      this.testResult = {};
    }

    this.testResult.testedAction = {};
    this.testResult.testedAction.httpMethod = this.httpMethod;
    this.testResult.testedAction.url = this.url;
    this.testResult.testedAction.headers = this.headers;
    this.testResult.testedAction.body = this.body;
    this.testResult.testedAction.tokenId = this.tokenId;
    this.testResult.testedAction.tokenIds = this.tokenIds;
  }

  rollbackAllAttributes() {
    this.rollbackAttributes();
    this.ruleset.rollbackAttributes();
  }

  get appPackageName() {
    return humanize([this.appPackageCode]);
  }

  @action
  switchToEveryone() {
    this.audienceMode = 'everyone';
    this.selectedSegmentIds = [];
  }

  @action
  switchToCustom() {
    this.audienceMode = 'custom';
    this.selectedSegmentIds = [];
  }

  @action
  toggleSegment(segmentId: number) {
    let currentIds = [...this.selectedSegmentIds];
    let index = currentIds.indexOf(segmentId);

    if (index >= 0) {
      currentIds.splice(index, 1);
    } else {
      currentIds.push(segmentId);
    }

    this.selectedSegmentIds = currentIds;

    // Set mode based on whether segments are selected
    if (currentIds.length > 0) {
      this.audienceMode = 'fin_audience';
    } else {
      this.audienceMode = 'everyone'; // No segments = everyone
    }
  }

  get hasAudienceChanges(): boolean {
    let modeChanged = this.audienceMode !== this._originalAudienceMode;
    let segmentsChanged =
      JSON.stringify([...this.selectedSegmentIds].sort()) !==
      JSON.stringify([...this._originalSelectedSegmentIds].sort());
    return modeChanged || segmentsChanged;
  }

  get selectedSegmentCount(): number {
    return this.selectedSegmentIds.length;
  }

  isSegmentSelected(segmentId: number): boolean {
    return this.selectedSegmentIds.includes(segmentId);
  }

  get isCustomMode(): boolean {
    return this.audienceMode === 'custom';
  }

  updatePredicates(predicates: any) {
    this.ruleset.set('predicateGroup.predicates', copy(predicates));
  }

  updateRolePredicates(rolePredicates: any) {
    this.ruleset.set('rolePredicateGroup.predicates', copy(rolePredicates));
  }

  updateOtpAuthEnabled() {
    this.otpAuthEnabled = !this.otpAuthEnabled;
    if (this.otpAuthEnabled) {
      this.setNoAuthenticationChosen(false);
    }
  }

  toggleFinWriteAction() {
    this.finWriteAction = !this.finWriteAction;
  }

  getAttributeNamesAndTypes() {
    let attributeData: Record<string, { name: string; type: string }> = {};

    this.allAttributes.forEach((attribute: any) => {
      attributeData[attribute.identifier] = { name: attribute.name, type: attribute.type };
    });

    this.resolver.eventAttributes.forEach((attribute: any) => {
      attributeData[attribute.templatable_identifier] = {
        name: attribute.name,
        type: attribute.type,
      };
    });
    return attributeData;
  }

  get resolver(): any {
    return new AttributeInfoResolver({
      attributes: this.attributeService?.allAttributes || [],
      includeAppAttributes: true,
    });
  }

  get allAttributes() {
    if (!this.attributeService) {
      return [];
    }
    return [...this.attributeService.allAttributes, ...this.resolver.manualAppAttributes];
  }

  getAttributeName(identifier: string): string | undefined {
    return this.attributeData[identifier]?.name;
  }

  refreshAttributeNames() {
    this.attributeData = this.getAttributeNamesAndTypes();
  }

  get enabledParamDescriptorNames(): string[] {
    return this.paramDescriptors
      .filter((descriptor) => descriptor.enabled)
      .map((descriptor) => descriptor.name);
  }

  get isMultiTokenEnabled(): boolean {
    return this.appService?.app?.canUseFeature?.('tdf-data-connectors-multi-token') || false;
  }

  getAudienceChangePayload() {
    if (!this.appService?.app?.canUseFeature?.('tdf-migrate-actions-to-fin-audience')) {
      return null;
    }

    let currentIds = this.selectedSegmentIds;
    let originalIds = this._originalSelectedSegmentIds;

    let currentSet = new Set(currentIds);
    let originalSet = new Set(originalIds);

    let segmentIdsToAdd = currentIds.filter((id) => !originalSet.has(id));
    let segmentIdsToDelete = originalIds.filter((id) => !currentSet.has(id));

    return {
      segmentIdsToAdd,
      segmentIdsToDelete,
      currentMode: this.audienceMode,
      currentSegmentIds: currentIds,
    };
  }
}
